import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import BranchFilter from "@/components/BranchFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import ServiceInvoiceDrawer from "@/components/ServiceInvoiceDrawer";
import JobCardsTab from "@/pages/service/JobCardsTab";
import SparesTab from "@/pages/service/SparesTab";
import ServiceAdvancesTab from "@/pages/service/ServiceAdvancesTab";
import { InvoiceGatePassButton } from "@/components/GatePassButtons";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Wrench, Package, Wallet, ShoppingCart, ClipboardText, ShieldCheck } from "@phosphor-icons/react";

/**
 * Service module — Phase 1: Invoices revamp.
 * Top-level sections (from the Feb-2026 revamp):
 *   • Invoices        — Job Card / Counter Sale / Other (AMC, RSA, ATW, Insurance)
 *   • Spares          — Phase 2 (JC issuance, warranty, inward, outward)
 *   • Service Advances — Phase 3 (Battery warranty, Accident, Direct parts)
 *   • Spares Order    — Phase 4 (Required, Actual, Delta, Ad-hoc from JC)
 */
const TOP_TABS = [
  { id: "invoices", label: "Invoices", icon: Wrench, ready: true },
  { id: "addons", label: "AMC / Add-ons", icon: ShieldCheck, ready: true },
  { id: "jobcards", label: "Job Cards", icon: ClipboardText, ready: true },
  { id: "spares", label: "Spares", icon: Package, ready: true },
  { id: "advances", label: "Service Advances", icon: Wallet, ready: true },
  { id: "orders", label: "Spares Order", icon: ShoppingCart, ready: true, route: "/spare-parts" },
];

// Invoices sub-tabs — server-side filtered via `invoice_type`. The
// "Other" type (AMC / RSA / ATW / Insurance Renewal) has its own top-tab
// section so it doesn't muddy the main revenue view.
const INVOICE_TABS = [
  { id: "all", label: "All", type: "", subtype: "" },
  { id: "jobcard", label: "Job Card", type: "jobcard", subtype: "" },
  { id: "counter_sale", label: "Counter Sale", type: "counter_sale", subtype: "" },
];

const OTHER_SUBTYPES = [
  { id: "", label: "All Other" },
  { id: "amc", label: "AMC" },
  { id: "rsa", label: "RSA" },
  { id: "atw", label: "ATW" },
  { id: "insurance_renewal", label: "Insurance Renewal" },
];

const SUBTYPE_LABEL = {
  amc: "AMC",
  rsa: "RSA",
  atw: "ATW",
  insurance_renewal: "Insurance Renewal",
};

const TYPE_LABEL = {
  jobcard: "Job Card",
  counter_sale: "Counter Sale",
  other: "Other",
};

export default function ServicePage() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canCreate = user?.role === "admin" || perms.includes("service.invoices.create");
  const canEdit = user?.role === "admin" || perms.includes("service.invoices.manage");
  const canDelete = user?.role === "admin" || perms.includes("service.invoices.delete");

  const [topTab, setTopTab] = useState("invoices");

  return (
    <div data-testid="service-page">
      <PageHeader
        eyebrow="05 / Service"
        title="Service"
        subtitle="Workshop invoices, spare movements, advances and stock reconciliation — one home for the service business."
      />
      <div className="px-8 py-6 space-y-4">
        <div className="inline-flex border border-canvas-line" data-testid="service-top-tabs">
          {TOP_TABS.map((t, i) => {
            const Icon = t.icon;
            const active = topTab === t.id;
            const commonCls = `inline-flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-wider ${
              i > 0 ? "border-l border-canvas-line" : ""
            } ${active ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`;
            // Some tabs (Spares Order) navigate to a full-page route
            // instead of switching the inline panel, because the
            // required-order workflow needs the extra real estate.
            if (t.route) {
              return (
                <Link
                  key={t.id}
                  to={t.route}
                  data-testid={`service-top-tab-${t.id}`}
                  className={commonCls}
                >
                  <Icon size={14} weight="bold" />
                  {t.label}
                </Link>
              );
            }
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTopTab(t.id)}
                data-testid={`service-top-tab-${t.id}`}
                className={commonCls}
              >
                <Icon size={14} weight="bold" />
                {t.label}
                {!t.ready && (
                  <span className="ml-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-wider border-current opacity-70">
                    {t.phase}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {topTab === "invoices" && (
          <InvoicesSection
            mode="standard"
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}
        {topTab === "addons" && (
          <InvoicesSection
            mode="addons"
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}
        {topTab === "jobcards" && (
          <JobCardsTab
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}
        {topTab === "spares" && (
          <SparesTab canCreate={canCreate} canDelete={canDelete} />
        )}
        {topTab === "advances" && (
          <ServiceAdvancesTab canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
        )}
      </div>
    </div>
  );
}

function ComingSoonPanel({ section, phase }) {
  return (
    <div
      className="border border-dashed border-canvas-line bg-canvas-card p-12 text-center"
      data-testid={`service-coming-soon-${section.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">{phase}</div>
      <div className="mt-2 font-display text-xl font-bold">{section} — coming next</div>
      <div className="mt-2 text-sm text-ink-muted max-w-lg mx-auto">
        This section is on the roadmap for the current service-module revamp. Ping when you want to
        prioritise it next.
      </div>
    </div>
  );
}

function InvoicesSection({ mode = "standard", canCreate, canEdit, canDelete }) {
  const isAddons = mode === "addons";
  // In addons mode the section always filters to invoice_type=other and
  // surfaces the AMC / RSA / ATW / Insurance Renewal picker instead of the
  // three main sub-tabs.
  const [invTab, setInvTab] = useState(isAddons ? "other" : "all");
  const [otherSubtype, setOtherSubtype] = useState("");
  const [branchId, setBranchId] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: ymd(start), to: ymd(today) };
  });
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, editing: null });

  const currentType = isAddons
    ? "other"
    : (INVOICE_TABS.find((t) => t.id === invTab)?.type || "");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      if (currentType) params.invoice_type = currentType;
      if (currentType === "other" && otherSubtype) params.invoice_subtype = otherSubtype;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/service/invoices", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invTab, otherSubtype, branchId, dateRange.from, dateRange.to, isAddons]);

  useEffect(() => {
    if (!isAddons && invTab !== "other" && otherSubtype) setOtherSubtype("");
  }, [invTab, otherSubtype, isAddons]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        count: acc.count + 1,
        total: acc.total + (Number(r.total_invoice_value) || 0),
        cash: acc.cash + (r.payment_method === "cash" ? Number(r.payment_amount) || 0 : 0),
        upi: acc.upi + (r.payment_method === "upi" ? Number(r.payment_amount) || 0 : 0),
        tax: acc.tax + (Number(r.tax_amount) || 0),
      }),
      { count: 0, total: 0, cash: 0, upi: 0, tax: 0 },
    );
  }, [rows]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete service invoice #${row.invoice_number}? This is a soft delete.`)) return;
    try {
      await api.delete(`/service/invoices/${row.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      {!isAddons && (
        <div className="inline-flex border border-canvas-line" data-testid="invoice-sub-tabs">
          {INVOICE_TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setInvTab(t.id)}
              data-testid={`invoice-sub-tab-${t.id}`}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${
                i > 0 ? "border-l border-canvas-line" : ""
              } ${invTab === t.id ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {isAddons && (
        <div className="flex flex-wrap gap-1" data-testid="addons-subtype-filter">
          {OTHER_SUBTYPES.map((s) => (
            <button
              key={s.id || "all"}
              type="button"
              onClick={() => setOtherSubtype(s.id)}
              data-testid={`addons-subtype-${s.id || "all"}`}
              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider border ${
                otherSubtype === s.id
                  ? "bg-ink text-white border-ink"
                  : "border-canvas-line text-ink-muted hover:border-ink"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <BranchFilter value={branchId} onChange={setBranchId} testId="service-branch-filter" kind="service" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="service-date" />
        <div className="flex items-center border border-canvas-line bg-canvas">
          <MagnifyingGlass size={12} weight="bold" className="mx-2 text-ink-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search invoice / JC # / vehicle / UTR"
            data-testid="service-search"
            className="px-1 py-2 bg-canvas text-sm focus:outline-none w-64"
          />
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setDrawer({ open: true, editing: null })}
            data-testid="new-service-invoice-button"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-ink/90"
          >
            <Plus size={12} weight="bold" /> New Invoice
          </button>
        )}
      </div>

      <div className={`grid gap-2 ${isAddons ? "grid-cols-5" : "grid-cols-4"}`} data-testid="service-kpis">
        <Kpi label="Invoices" value={totals.count} />
        <Kpi label="Total ₹" value={fmtMoney(totals.total)} />
        <Kpi label="Cash ₹" value={fmtMoney(totals.cash)} />
        <Kpi label="UPI ₹" value={fmtMoney(totals.upi)} />
        {isAddons && <Kpi label="Tax ₹" value={fmtMoney(totals.tax)} />}
      </div>

      <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="service-invoices-table">
          <thead className="bg-canvas-panel">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Inv #</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">JC #</th>
              <th className="px-3 py-2.5">Vehicle / Customer</th>
              <th className="px-3 py-2.5 text-right">Total ₹</th>
              <th className="px-3 py-2.5">Pay</th>
              {isAddons && <th className="px-3 py-2.5 text-right">Tax ₹</th>}
              <th className="px-3 py-2.5">Technician / Advisor</th>
              <th className="px-3 py-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isAddons ? 10 : 9} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={isAddons ? 10 : 9} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">
                No invoices match — try widening the date range or clearing filters.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id}
                  data-testid={`service-row-${r.id}`}
                  className="border-t border-canvas-line hover:bg-canvas-panel/40">
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-mono">{r.invoice_number}</td>
                <td className="px-3 py-2.5">
                  <TypeChip type={r.invoice_type} subtype={r.invoice_subtype} />
                </td>
                <td className="px-3 py-2.5 font-mono text-ink-muted">{r.job_card_number || "—"}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{r.vehicle_registration_no || "—"}</div>
                  <div className="text-xs text-ink-muted">{r.customer_name || ""}</div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmtMoney(r.total_invoice_value)}</td>
                <td className="px-3 py-2.5">
                  <PaymentCell row={r} />
                </td>
                {isAddons && (
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {r.tax_amount > 0 ? fmtMoney(r.tax_amount) : "—"}
                  </td>
                )}
                <td className="px-3 py-2.5 text-xs">
                  <div>{r.technician || "—"}</div>
                  <div className="text-ink-muted">{r.advisor || ""}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <InvoiceGatePassButton invoice={r} />
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setDrawer({ open: true, editing: r })}
                        data-testid={`edit-service-${r.id}`}
                        className="p-1.5 border border-canvas-line hover:border-ink"
                        title="Edit"
                      >
                        <PencilSimple size={12} weight="bold" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(r)}
                        data-testid={`delete-service-${r.id}`}
                        className="p-1.5 border border-canvas-line hover:border-destructive text-destructive"
                        title="Delete"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drawer.open && (
        <ServiceInvoiceDrawer
          editing={drawer.editing}
          branchId={branchId}
          defaultType={isAddons ? "other" : "jobcard"}
          onClose={() => setDrawer({ open: false, editing: null })}
          onSaved={() => { setDrawer({ open: false, editing: null }); load(); }}
        />
      )}
    </>
  );
}


function PaymentCell({ row }) {
  const cashPaid = (row.payment_lines || []).reduce((s, pl) => s + (Number(pl.amount) || 0), 0)
    || Number(row.payment_amount || 0);
  const advancePaid = Number(row.advance_paid_amount || 0);
  const methods = [];
  if (advancePaid > 0) methods.push("ADV");
  if (cashPaid > 0) methods.push((row.payment_method || "cash").toUpperCase());
  const status = row.payment_status || "pending";
  const statusCls = status === "paid"
    ? "border-[#00713a] text-[#00713a]"
    : status === "partial"
    ? "border-amber-500 text-amber-700"
    : "border-canvas-line text-ink-muted";
  return (
    <div className="space-y-1" data-testid={`service-payment-cell-${row.id}`}>
      <div className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${statusCls}`}>
        {status}
      </div>
      <div className="text-[10px] font-mono uppercase text-ink-muted">
        {methods.length ? methods.join(" + ") : "—"}
      </div>
      {Number(row.balance_due || 0) > 0 && (
        <div className="text-[10px] font-mono text-amber-700">
          Due {fmtMoney(row.balance_due)}
        </div>
      )}
      {row.payment_method === "upi" && row.upi_ref && cashPaid > 0 && (
        <div className="text-[10px] font-mono text-ink-muted">{row.upi_ref}</div>
      )}
    </div>
  );
}

function TypeChip({ type, subtype }) {
  const t = type || "jobcard";
  const label = TYPE_LABEL[t] || t;
  const sub = subtype ? SUBTYPE_LABEL[subtype] || subtype : "";
  const cls = t === "jobcard"
    ? "border-[#5f3dc4] text-[#5f3dc4]"
    : t === "counter_sale"
    ? "border-[#1971c2] text-[#1971c2]"
    : "border-[#E08600] text-[#A35700]";
  return (
    <div className="inline-flex flex-col gap-0.5">
      <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${cls}`}>
        {label}
      </span>
      {sub && (
        <span className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">{sub}</span>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-lg font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}
